import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicLinkInfo, submitPublicIncident } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  AlertTriangle, 
  Loader2, 
  CheckCircle2, 
  Building2,
  Calendar,
  MapPin,
  User,
  Mail,
  Phone,
  FileText,
  Shield
} from 'lucide-react';

const CATEGORIES = [
  { value: 'abuse_misconduct', label: 'Abuse / Misconduct' },
  { value: 'injury_medical', label: 'Injury / Medical' },
  { value: 'facility_premises', label: 'Facility / Premises' },
  { value: 'equipment', label: 'Equipment Issue' },
  { value: 'operational', label: 'Operational' },
  { value: 'behavior_conduct', label: 'Behavior / Conduct' },
  { value: 'environmental', label: 'Environmental' },
  { value: 'other_safety', label: 'Other Safety Concern' },
];

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low - Minor issue' },
  { value: 'medium', label: 'Medium - Moderate concern' },
  { value: 'high', label: 'High - Serious issue' },
  { value: 'critical', label: 'Critical - Requires immediate attention' },
];

export default function PublicIncidentReport() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referenceId, setReferenceId] = useState('');
  const [error, setError] = useState(null);
  const [orgInfo, setOrgInfo] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    incident_date: new Date().toISOString().split('T')[0],
    incident_time: '',
    location: '',
    primary_category: '',
    severity: 'medium',
    is_anonymous: true,
    reporter_name: '',
    reporter_email: '',
    reporter_phone: '',
    witnesses: '',
    additional_details: '',
  });

  useEffect(() => {
    const fetchLinkInfo = async () => {
      try {
        const response = await getPublicLinkInfo(token);
        setOrgInfo(response.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('This link is not valid or has been deactivated.');
        } else if (err.response?.status === 410) {
          setError(err.response?.data?.detail || 'This link has expired.');
        } else {
          setError('Unable to load the reporting form. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchLinkInfo();
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please provide a title for the incident');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Please describe what happened');
      return;
    }
    if (!formData.primary_category) {
      toast.error('Please select a category');
      return;
    }

    setSubmitting(true);
    try {
      const response = await submitPublicIncident(token, formData);
      setReferenceId(response.data.reference_id);
      setSubmitted(true);
      toast.success('Incident report submitted successfully');
    } catch (err) {
      if (err.response?.status === 410) {
        setError(err.response?.data?.detail || 'This link has expired.');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to submit report');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Unable to Access Form</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Report Submitted</h2>
            <p className="text-muted-foreground mb-4">
              Thank you for your report. It has been submitted successfully.
            </p>
            {referenceId && (
              <div className="bg-muted rounded-lg p-4 mb-4">
                <p className="text-sm text-muted-foreground">Your reference number:</p>
                <p className="text-2xl font-mono font-bold">{referenceId}</p>
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              You may close this window. The organization will review your report.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              {orgInfo?.organization_logo ? (
                <img 
                  src={orgInfo.organization_logo} 
                  alt={orgInfo.organization_name}
                  className="w-16 h-16 object-contain rounded-lg"
                />
              ) : (
                <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-primary" />
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold">{orgInfo?.organization_name}</h1>
                <p className="text-muted-foreground">Anonymous Incident Reporting</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Submit an Incident Report
            </CardTitle>
            <CardDescription>
              Your report will be kept confidential. Anonymous reporting is available.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Incident Details */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Incident Details
                </h3>
                
                <div>
                  <Label htmlFor="title">Title / Summary *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Brief summary of the incident"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="description">What Happened? *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Please describe the incident in detail..."
                    rows={4}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="incident_date">Date of Incident *</Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="incident_date"
                        type="date"
                        value={formData.incident_date}
                        onChange={(e) => setFormData({ ...formData, incident_date: e.target.value })}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="incident_time">Time (if known)</Label>
                    <Input
                      id="incident_time"
                      type="time"
                      value={formData.incident_time}
                      onChange={(e) => setFormData({ ...formData, incident_time: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="location">Location</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="Where did this occur?"
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Category *</Label>
                    <Select 
                      value={formData.primary_category} 
                      onValueChange={(value) => setFormData({ ...formData, primary_category: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Severity *</Label>
                    <Select 
                      value={formData.severity} 
                      onValueChange={(value) => setFormData({ ...formData, severity: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SEVERITY_OPTIONS.map((sev) => (
                          <SelectItem key={sev.value} value={sev.value}>
                            {sev.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="witnesses">Witnesses (if any)</Label>
                  <Textarea
                    id="witnesses"
                    value={formData.witnesses}
                    onChange={(e) => setFormData({ ...formData, witnesses: e.target.value })}
                    placeholder="Names or descriptions of any witnesses"
                    rows={2}
                  />
                </div>

                <div>
                  <Label htmlFor="additional_details">Additional Details</Label>
                  <Textarea
                    id="additional_details"
                    value={formData.additional_details}
                    onChange={(e) => setFormData({ ...formData, additional_details: e.target.value })}
                    placeholder="Any other information that may be helpful..."
                    rows={3}
                  />
                </div>
              </div>

              {/* Reporter Information */}
              <div className="space-y-4 pt-6 border-t">
                <h3 className="font-semibold flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Your Information (Optional)
                </h3>

                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="anonymous" 
                    checked={formData.is_anonymous}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_anonymous: checked })}
                  />
                  <Label htmlFor="anonymous" className="cursor-pointer">
                    Submit anonymously (recommended for sensitive matters)
                  </Label>
                </div>

                {!formData.is_anonymous && (
                  <div className="space-y-4 pl-6 border-l-2 border-muted">
                    <div>
                      <Label htmlFor="reporter_name">Your Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="reporter_name"
                          value={formData.reporter_name}
                          onChange={(e) => setFormData({ ...formData, reporter_name: e.target.value })}
                          placeholder="Full name"
                          className="pl-10"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="reporter_email">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="reporter_email"
                          type="email"
                          value={formData.reporter_email}
                          onChange={(e) => setFormData({ ...formData, reporter_email: e.target.value })}
                          placeholder="email@example.com"
                          className="pl-10"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="reporter_phone">Phone Number</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="reporter_phone"
                          type="tel"
                          value={formData.reporter_phone}
                          onChange={(e) => setFormData({ ...formData, reporter_phone: e.target.value })}
                          placeholder="(555) 123-4567"
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-6 border-t">
                <Button 
                  type="submit" 
                  className="w-full" 
                  size="lg"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting Report...
                    </>
                  ) : (
                    'Submit Incident Report'
                  )}
                </Button>
                <p className="text-xs text-center text-muted-foreground mt-4">
                  By submitting this report, you confirm that the information provided is accurate to the best of your knowledge.
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
